import {Location} from '@angular/common';
import {Component, DoCheck, OnDestroy, OnInit} from '@angular/core';
import {animate, style, transition, trigger} from '@angular/animations';
import {ActivatedRoute, Router} from '@angular/router';
import {debounceTime, interval, Subject, Subscription} from 'rxjs';
import {URLS} from '../../../data/navigation/navigation.data';
import {THEMES, TOOLS} from '../../../data/product/product.data';
import {THEME_PALETTES} from '../../../data/palette/palette.data';
import {PLATFORM_VERSIONS, STORE_PLATFORM_VERSIONS} from '../../../data/product/platform-version.data';
import {ThemePalette} from '../../../model/palette/palette.model';
import {combinedRating, marketplaceVotes, Platform, Product, platformCtaLabel, platformSku, isThemeFree, isPlatformPaid, isPlatformPlanned, noRatingLabel, sortPlatformsByCoverage, platformListPrice, PLATFORM_CTA_LABELS, platformCtaKind } from '../../../model/product/product.model';
import {FlatPlatform} from '../../../model/product/flat-platform.model';
import {Image} from '../../../model/image/image.model';
import {ColorService} from '../../../service/color/color.service';
import {ThemeService} from '../../../service/theme/theme.service';
import {ProductService} from '../../../service/product/product.service';
import {SeoService} from '../../../service/seo/seo.service';
import {Clipboard} from '@angular/cdk/clipboard';
import {forkJoin, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import { MonitorProviderService } from '../../../service/provider/monitor-provider.service';
import {UtilService} from '../../../service/util/util.service';
import {RoutingService} from '../../../service/routing/routing.service';
import {CartPopupService} from '../../../service/cart/cart-popup.service';
import {StoreService} from '../../../service/store/store.service';
import {Review} from '../../../model/review/review.model';
import {marketplaceReviewCards, storeReviewCards} from '../../../util/review-cards';
import { DirectDownloadService } from '../../../service/store/direct-download.service';

@Component({
  selector: 'app-theme-page',
  templateUrl: './theme-page.component.html',
  styleUrls: ['./theme-page.component.scss'],
  animations: [
    trigger('fadeEffect', [
      // One-time entrance fade as the banner first renders.
      transition(':enter', [
        style({opacity: 0}),
        animate('1s ease-in-out', style({opacity: 1}))
      ])
    ]),
    trigger('slideEffect', [
      // Screenshot slides in from the right both on first render and on
      // every platform switch. Bound to a key composed of product+platform
      // so a value change re-fires the void->* transition.
      transition('* => *', [
        style({transform: 'translateX(40%)', opacity: 0}),
        animate('550ms cubic-bezier(0.22, 1, 0.36, 1)',
                style({transform: 'translateX(0)', opacity: 1}))
      ])
    ]),
    trigger('logoSlide', [
      // On platform change the old logo slides out to the left while the new one
      // slides in from the right at the same time (clipped to the round wrapper).
      transition(':enter', [
        style({transform: 'translateX(115%)', opacity: 0}),
        animate('550ms cubic-bezier(0.22, 1, 0.36, 1)',
                style({transform: 'translateX(0)', opacity: 1}))
      ]),
      transition(':leave', [
        animate('550ms cubic-bezier(0.22, 1, 0.36, 1)',
                style({transform: 'translateX(-115%)', opacity: 0}))
      ])
    ])
  ],
})
export class ThemePageComponent implements DoCheck, OnInit, OnDestroy {

  URLS = URLS;
  TOOLS = TOOLS;

  palette = [];
  // The theme's curated curated named palette (the palettes product);
  // rendered as the page's top section, linking to /color-palette.
  themePalette: ThemePalette | null = null;
  // Plain hex list for <app-color-detail> (stable reference, rebuilt only when
  // the palette changes - so it doesn't retrigger the child every change cycle).
  paletteHexes: string[] = [];

  product: Product = undefined;
  category;

  // Which platform (in the flattened platform list) is currently selected.
  // Drives both the highlighted icon and which screenshot the banner shows.
  selectedPlatformIndex = 0;
  screenshotSubscription: Subscription;
  private themeSub: Subscription;
  private routeSub: Subscription;

  // The screenshot the banner is actually rendering. Lags one step behind
  // `selectedScreenshot` while a preload is in flight, so the slide
  // animation always plays against the new (loaded) image rather than
  // the previous one.
  displayedScreenshot: any = null;
  private lastTargetScreenshotSrc = '';

  harmonyResult = [];

  analogous = [];
  complementaries = [];
  triadics = [];
  temperatures = [];

  protanomalyColors = [];
  deuteranomalyColors = [];
  tritanomalyColors = [];
  protanopiaColors = [];
  deuteranopiaColors = [];
  tritanopiaColors = [];
  achromatomalyColors = [];
  achromatopsiaColors = [];

  selectedColorBlindnessSubType = 'protanomaly';
  selectedColorBlindnessObject = {
    title: '',
    explanation: '',
    colors: [],
  };

  themesWallAnimating = true;

  darkModeActive = false;

  downloadsLoaded = false;
  bannerImageLoaded = false;
  animatedDownloads = '0';
  downloadAnimationInterval?: any;
  actualDownloadsTotal = 0;

  // A live theme always reports downloads; a 0 total means the stats call
  // failed or returned empty, so we retry a few times before showing a
  // temporary error in place of the counter.
  downloadsError = false;
  private statsAttempt = 0;
  private readonly maxStatsAttempts = 5;

  THEMES_WALL = [];

  // Store: formatted price per sku ("$1.99") from the live catalogue, and the
  // skus currently in the cart - both drive the platform pill (price flag,
  // "Add to cart" / "In cart" CTA) for listings sold on the site.
  priceBySku: { [ sku: string ]: string | null } = {};
  /** the catalogue's version per sku ("2026.1.1"), shown in the pill after the platform name (owner, 2026-09-20) */
  versionBySku: { [ sku: string ]: string } = {};
  private storeSubs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private colorService: ColorService,
    public utilService: UtilService,
    private monitorProviderService: MonitorProviderService,
    private routingService: RoutingService,
    private themeService: ThemeService,
    private cartPopupService: CartPopupService,
    private storeService: StoreService,
    private directDownloadService: DirectDownloadService) {
  }

  ngOnInit(): void {
    this.storeSubs.push(
      this.storeService.catalogBySku$.subscribe( bySku => {
        const prices: { [ sku: string ]: string | null } = {};
        const versions: { [ sku: string ]: string } = {};
        for ( const sku of Object.keys( bySku ) ) {
          prices[ sku ] = this.storeService.formatPrice( bySku[ sku ].unitAmount, bySku[ sku ].currency );
          versions[ sku ] = bySku[ sku ].version || '';
        }
        this.priceBySku = prices;
        this.versionBySku = versions;
      } ),
    );
    this.themeSub = this.themeService.darkLightSetting.subscribe( ( darkLightSetting ) => {
      this.darkModeActive = this.themeService.selectDarkLightByCSS( false, true );
    } );

    this.downloadsLoaded = false;
    this.routeSub = this.route.queryParams.subscribe(params => {
      // A theme page needs a product; the category is derivable from it (the
      // lookup below scans every line anyway), so a bare /theme?product=miami
      // from an old link still renders instead of soft-404ing on the client.
      if (!params.product) {
        this.router.navigate([URLS.notFound]);
        return;
      }
      if (params.product) {
        this.product = undefined;
        this.reviewCards = [];
        this.bannerImageLoaded = false;
        setTimeout(() => {
          const element = document.getElementById('head');
          if (element) {
            element.scrollIntoView({behavior: 'smooth', block: 'start'});
          }
        }, 100);

        const themes = THEMES.map(productLine => productLine.products).reduce((acc, val) => acc.concat(val), []);
        // Match the product key case-insensitively so /theme?product=Istanbul
        // resolves the same theme as ?product=istanbul instead of rendering an
        // empty banner when the URL's casing differs from the canonical key.
        const requestedKey = String(params.product).toLowerCase();
        this.product = themes.find(product => product.key.toLowerCase() === requestedKey);
        if (!this.product) {
          // Unknown product key -> not a real theme page.
          this.router.navigate([URLS.notFound]);
          return;
        }
        if (this.product) {
          this.product.rating = undefined;
          this.downloadsError = false;
          this.statsAttempt = 0;
          this.startDownloadAnimation();

          if ( this.utilService.isExternalIdExist( this.product ) ) {
            this.fetchStats();
          } else {
            this.product.downloads = this.utilService.numberFormatter( 0 );
            this.actualDownloadsTotal = 0;
            this.downloadsLoaded = true;
          }

          this.themePalette = THEME_PALETTES.find( p => p.key === this.product.key ) ?? null;

          const palette = this.product.palette;

          this.palette = [];
          for (const item of palette) {
            if (!this.colorService.isValidHexCode(item.replace('#', ''))) {
              this.router.navigate([URLS.notFound]);
              return;
            }
            this.palette.push({
              value: item.toLowerCase(),
              name: this.colorService.getColorName(item)
            });
          }
          this.paletteHexes = this.palette.map(p => p.value);
          this.bundleSwatch = this.product?.isBundle
            ? ( [] as Product[] ).concat( ...THEMES.map( line => line.products ) ).filter( t => !t.isBundle ).map( t => t.designTokens?.primaryColor ).filter( Boolean ).slice( 0, 6 ) as string[]
            : [];

          this.updateColors(false);

          this.THEMES_WALL = [];
          for (const productLine of THEMES) {
            for (const theme of productLine.products) {
              if (theme.key !== this.product.key) {
                this.THEMES_WALL.push({
                  ...theme,
                  category: productLine.name
                });
              }
            }
          }

          if (this.THEMES_WALL.length > 0 && this.THEMES_WALL.length < 10) {
            this.THEMES_WALL = [...this.THEMES_WALL, ...this.THEMES_WALL, ...this.THEMES_WALL];
          }

          this.platformParam = params.platform;
          this.startScreenshotCarousel( params.platform );
        }
      }

      if (params.category) {
        this.category = params.category;
      } else if (this.product) {
        // No category in the URL: derive it from the product's own line.
        const line = THEMES.find(l => l.products.some(p => p.key === this.product.key));
        this.category = line ? line.name : '';
      }
    });
  }

  private startDownloadAnimation() {
    if ( !this.product?.platforms || this.product.platforms.length === 0 ) {
      this.animatedDownloads = '';
      return;
    }

    this.downloadAnimationInterval = this.utilService.startDownloadAnimation(
      () => this.downloadsLoaded,
      ( value ) => this.animatedDownloads = value,
      ( finalValue ) => {
        this.animatedDownloads = finalValue;
        this.downloadAnimationInterval = undefined;
      },
      () => this.actualDownloadsTotal
    );
  }

  ngOnDestroy(): void {
    this.storeSubs.forEach( sub => sub.unsubscribe() );
    this.storeSubs = [];
    this.stopScreenshotCarousel();
    this.themeSub?.unsubscribe();
    this.routeSub?.unsubscribe();
    if ( this.downloadAnimationInterval ) {
      this.downloadAnimationInterval();
    }
  }

  /**
   * Fetches download/rating stats for the current product. A live theme always
   * reports downloads, so a 0 total is treated as a transient failure and
   * retried up to `maxStatsAttempts` times before a temporary error is shown.
   */
  private fetchStats(): void {
    forkJoin( [ this.monitorProviderService.getStats( this.product.key ), this.storeService.reviews() ] ).subscribe( {
      next: ( [ results, store ] ) => {
        let total = 0;
        ( results || [] ).forEach( ( platformData: any ) => {
          // Chrome reports installs as downloads once the monitor has them; its weekly
          // "users" are the same people, so they only stand in while downloads are 0.
          total += platformData.downloads || platformData.users || 0;
        } );
        // one rating over every marketplace and the site's own buyers (-1 = loaded, none yet)
        this.product.rating = combinedRating( results, store?.products?.[ this.product.key ] ) || -1;
        this.reviewOtherCount = marketplaceVotes( results );
        // the theme's own comments, from the marketplaces and the site's buyers, for the bottom of the page
        this.reviewCards = [ ...storeReviewCards( this.product, store?.reviews ), ...marketplaceReviewCards( this.product, results ) ];

        // No rows at all = the monitor has nothing for this product yet (or the
        // request came back empty), worth a retry. Rows that genuinely add up
        // to zero (a freshly listed standalone premium theme, London on day
        // one) are a real answer: show 0, do not fail over to "unavailable".
        if ( total === 0 && ( results || [] ).length === 0 ) {
          this.retryStats();
          return;
        }

        if ( this.product.rating === undefined ) {
          this.product.rating = 0;
        }

        this.product.downloads = this.utilService.numberFormatter( total );
        this.actualDownloadsTotal = total;
        this.downloadsError = false;
        this.downloadsLoaded = true;
      },
      error: () => this.retryStats(),
    } );
  }

  /** Retries the stats call, or surfaces a temporary error after the cap. */
  private retryStats(): void {
    this.statsAttempt++;

    if ( this.statsAttempt < this.maxStatsAttempts ) {
      setTimeout( () => this.fetchStats(), 800 );
      return;
    }

    if ( this.product.rating === undefined ) {
      this.product.rating = 0;
    }

    this.downloadsError = true;
    this.downloadsLoaded = true;
  }

  private startScreenshotCarousel( platformParam?: string ) {
    this.stopScreenshotCarousel();
    const platforms = this.getVisiblePlatforms();
    const count = platforms.length;

    // When the URL pins a platform (?platform=Slack) start on that platform and
    // do NOT auto-cycle - the screenshot stays put (mirrors theme/content).
    // Falls through to the default if the platform name isn't found.
    if ( platformParam ) {
      const idx = platforms.findIndex(
        p => p.name?.toLowerCase() === String( platformParam ).toLowerCase()
      );
      if ( idx >= 0 ) {
        this.selectedPlatformIndex = idx;
        return;
      }
    }

    this.selectedPlatformIndex = 0;
    // Bundle products drive a "This bundle includes" grid off the selected
    // platform; auto-cycling would make that grid jump between platforms, so
    // pin to the first platform and let the user switch manually.
    if (count > 1 && !this.product?.isBundle) {
      this.screenshotSubscription = interval(8000).subscribe(() => {
        // Cycle through platforms automatically. User clicks on a platform
        // icon reset this via selectPlatform().
        this.selectedPlatformIndex = (this.selectedPlatformIndex + 1) % count;
      });
    }
  }

  private stopScreenshotCarousel() {
    if (this.screenshotSubscription) {
      this.screenshotSubscription.unsubscribe();
    }
  }

  public analyzePaletteHarmony() {
    const rgbPalette = this.palette.map(color => this.colorService.hexToRgb(color.value));

    const hsvPalette = rgbPalette.map(rgb => this.colorService.rgbToHsv(rgb));

    const hues = hsvPalette.map(hsv => hsv.h);

    let warmCount = 0;
    let coolCount = 0;

    const huePairs = new Set();
    const hueTriplets = new Set();

    this.temperatures = [];
    this.analogous = [];
    this.complementaries = [];
    this.triadics = [];
    for (let i = 0; i < hues.length; i++) {
      this.analogous.push(0);
      this.complementaries.push(0);
      this.triadics.push(0);
    }

    // Combine loops for hue comparison and warm/cool check
    for (let i = 0; i < hues.length; i++) {
      // Check if the hue is warm or cool
      if (this.colorService.isWarm(hues[i])) {
        warmCount++;
        this.temperatures.push('warm');
      } else if (this.colorService.isCool(hues[i])) {
        this.temperatures.push('cool');
        coolCount++;
      }

      for (let j = i + 1; j < hues.length; j++) {
        if (!huePairs.has(`${i},${j}`) && !huePairs.has(`${j},${i}`)) {
          if (this.colorService.isComplementary(hues[i], hues[j])) {
            this.complementaries[i] = this.complementaries[i] + 1;
            this.complementaries[j] = this.complementaries[j] + 1;
          }
          if (this.colorService.isAnalogous(hues[i], hues[j])) {
            this.analogous[i] = this.analogous[i] + 1;
            this.analogous[j] = this.analogous[j] + 1;
          }
          huePairs.add(`${i},${j}`);
        }
      }
    }

    for (let i = 0; i < hues.length - 2; i++) {
      for (let j = i + 1; j < hues.length - 1; j++) {
        for (let k = j + 1; k < hues.length; k++) {
          const triplet = [hues[i], hues[j], hues[k]].sort().join(',');
          if (!hueTriplets.has(triplet)) {
            if (this.colorService.isTriadic([hues[i], hues[j], hues[k]])) {
              this.triadics[i] = this.triadics[i] + 1;
              this.triadics[j] = this.triadics[j] + 1;
              this.triadics[k] = this.triadics[k] + 1;
            }
            hueTriplets.add(triplet);
          }
        }
      }
    }

    const temperatureCount = Math.max(warmCount, coolCount);

    let temperatureDecision = '';

    if (warmCount > coolCount) {
      temperatureDecision = 'warm';
    } else if (warmCount < coolCount) {
      temperatureDecision = 'cool';
    } else {
      temperatureDecision = 'equal';
    }

    // Score every scheme the same way as the tools: the number of palette colours
    // that take part (a plain count, not the number of combinations), so triadic's
    // triplets no longer inflate it. The ring fills by count / palette length.
    const participating = ( arr: number[] ) => arr.filter( c => c > 0 ).length;
    return [
      {
        name: 'Analogous',
        count: participating( this.analogous ),
        max: this.palette.length,
      },
      {
        name: 'Complementary',
        count: participating( this.complementaries ),
        max: this.palette.length,
      },
      {
        name: 'Triadic',
        count: participating( this.triadics ),
        max: this.palette.length,
      },
      {
        name: 'Temperature',
        count: temperatureCount,
        decision: temperatureDecision,
        max: this.palette.length,
      },
    ];
  }

  updateColors(removeProduct = true) {
    if (removeProduct === true) {
      this.product = undefined;
    }

    if (this.palette.length > 0) {
      this.protanomalyColors = this.colorService.generateColorBlindnessColorsForPalette(this.palette, 'protanomaly');
      this.deuteranomalyColors = this.colorService.generateColorBlindnessColorsForPalette(this.palette, 'deuteranomaly');
      this.tritanomalyColors = this.colorService.generateColorBlindnessColorsForPalette(this.palette, 'tritanomaly');
      this.protanopiaColors = this.colorService.generateColorBlindnessColorsForPalette(this.palette, 'protanopia');
      this.deuteranopiaColors = this.colorService.generateColorBlindnessColorsForPalette(this.palette, 'deuteranopia');
      this.tritanopiaColors = this.colorService.generateColorBlindnessColorsForPalette(this.palette, 'tritanopia');
      this.achromatomalyColors = this.colorService.generateColorBlindnessColorsForPalette(this.palette, 'achromatomaly');
      this.achromatopsiaColors = this.colorService.generateColorBlindnessColorsForPalette(this.palette, 'achromatopsia');
      this.harmonyResult = this.analyzePaletteHarmony();
      this.setSelectedColorBlindnessColors();
    } else {
      this.protanomalyColors = [];
      this.deuteranomalyColors = [];
      this.tritanomalyColors = [];
      this.protanopiaColors = [];
      this.deuteranopiaColors = [];
      this.tritanopiaColors = [];
      this.achromatomalyColors = [];
      this.achromatopsiaColors = [];
      this.harmonyResult = [];
    }
  }

  setColorBlindnessSubType(colorBlindnessSubType: string) {
    this.selectedColorBlindnessSubType = colorBlindnessSubType;
    this.setSelectedColorBlindnessColors();
  }

  private setSelectedColorBlindnessColors() {
    switch (this.selectedColorBlindnessSubType) {
      case 'protanomaly' :
      default:
        this.selectedColorBlindnessObject = {
          title: 'Protanomaly',
          explanation: 'Protanomaly is a type of red-weakness where the red photoreceptors in the eyes are less sensitive.\n' +
            '          People with protanomaly experience reds, oranges, and yellows as appearing greener and less bright.\n' +
            '          Everyday tasks that involve distinguishing between these colors can be more challenging, especially\n' +
            '          in low-light conditions. Protanomaly is the most common type of red-green color blindness.',
          colors: this.protanomalyColors
        };
        break;
      case 'deuteranomaly' :
        this.selectedColorBlindnessObject = {
          title: 'Deuteranomaly',
          explanation: 'Deuteranomaly is a type of green-weakness where the green photoreceptors are less sensitive.\n' +
            '          This condition causes greens, oranges, and reds to appear shifted towards red. As a result,\n' +
            '          it can be difficult to differentiate between these colors. Deuteranomaly is the most common form\n' +
            '          of color blindness, affecting up to 5% of males but is rare in females.',
          colors: this.deuteranomalyColors
        };
        break;
      case 'tritanomaly' :
        this.selectedColorBlindnessObject = {
          title: 'Tritanomaly',
          explanation: 'Tritanomaly is a type of blue-weakness where blue photoreceptors are less sensitive.\n' +
            '          This condition makes blues appear greener, and it can be hard to distinguish yellow and red\n' +
            '          from pink. Tritanomaly is quite rare compared to other types of color blindness and can affect\n' +
            '          the ability to perform tasks that rely on color differentiation, such as reading colored maps or graphs.',
          colors: this.tritanomalyColors
        };
        break;
      case 'achromatomaly' :
        this.selectedColorBlindnessObject = {
          title: 'Achromatomaly',
          explanation: 'Achromatomaly is a type of incomplete color blindness where there are fewer functioning cones in the retina.\n' +
            '          People with achromatomaly have limited color vision and often see a washed-out palette of colors.\n' +
            '          This condition can make the world appear less vibrant and can affect one\'s ability to perform tasks\n' +
            '          that require accurate color perception, such as selecting matching clothing or interpreting colored charts.',
          colors: this.achromatomalyColors
        };
        break;
      case 'protanopia' :
        this.selectedColorBlindnessObject = {
          title: 'Protanopia',
          explanation: 'Protanopia is a type of red-blindness where red photoreceptors are completely absent.\n' +
            '          People with protanopia have difficulty distinguishing between red and green colors, with reds\n' +
            '          appearing much darker than they are to those with normal vision. This condition can significantly\n' +
            '          impact daily activities, such as reading traffic lights or selecting ripe fruits.',
          colors: this.protanopiaColors
        };
        break;
      case 'deuteranopia' :
        this.selectedColorBlindnessObject = {
          title: 'Deuteranopia',
          explanation: 'Deuteranopia is a type of green-blindness where green photoreceptors are completely absent.\n' +
            '          This condition results in a similar effect to protanopia, causing difficulties in distinguishing\n' +
            '          between red and green colors. However, the brightness of colors is not as affected as in protanopia.\n' +
            '          Everyday activities, such as gardening or cooking, can be more challenging due to color confusion.',
          colors: this.deuteranopiaColors
        };
        break;
      case 'tritanopia' :
        this.selectedColorBlindnessObject = {
          title: 'Tritanopia',
          explanation: 'Tritanopia is a type of blue-blindness where blue photoreceptors are completely absent.\n' +
            '          This condition leads to difficulty distinguishing between blue and yellow colors, with blues\n' +
            '          often appearing as greenish and yellows appearing as light gray. Tritanopia is extremely rare\n' +
            '          and can impact various aspects of life, including fashion, art, and any tasks involving color coding.',
          colors: this.tritanopiaColors
        };
        break;
      case 'achromatopsia' :
        this.selectedColorBlindnessObject = {
          title: 'Achromatopsia',
          explanation: 'Achromatopsia is a type of complete color blindness where all cone photoreceptors are absent.\n' +
            '          Individuals with achromatopsia see the world in shades of gray, as they rely entirely on rod\n' +
            '          photoreceptors, which detect light intensity but not color. This condition can greatly impact\n' +
            '          daily life, as it affects the ability to perceive color-coded information and can reduce visual\n' +
            '          acuity, especially in bright light.',
          colors: this.achromatopsiaColors
        };
        break;
    }
  }

  openRouteInNewTab(route: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree([route]));
    window.open(url, '_blank');
  }

  redirect(themeItem: any) {
    this.routingService.redirect(URLS.theme, {product: themeItem.key, category: themeItem.category});
  }

  /** Curated names for the marketing palette hexes (tooltip labels in the
   *  Color Palette section) - looked up from the theme's named palette. */
  get paletteColorNames(): string[] {
    if ( !this.themePalette ) {
      return [];
    }
    return this.paletteHexes.map( hex => {
      const found = this.themePalette!.colors.find(
        c => c.hex.toLowerCase() === hex.toLowerCase()
      );
      return found?.name ?? '';
    } );
  }

  /** Open the themes gallery pre-filtered to the clicked tag. */
  goToThemesByTag(tag: string) {
    if (!tag) {
      return;
    }
    this.routingService.redirect(URLS.themes, {tag: tag.toLowerCase()});
  }

  onPlatformClick( platformName: string, url: string, downloadable = false ) {
    this.monitorProviderService.recordClick( this.product.key, platformName ).subscribe( {
      error: ( err ) => console.error( 'Failed to record platform click:', err )
    } );

    // the free listing outside the cart (2026-09-21, no theme/content page any more): Rhino's file
    // downloads on the spot; everything else links out
    if ( downloadable ) {
      if ( platformCtaKind( this.product, platformName ) === 'download' ) this.directDownloadService.downloadRhino( this.product, platformName, url );
      return;
    }

    window.open( url, '_blank' );
  }

  // Cached flattened platforms so *ngFor sees the same array reference on
  // every change-detection tick (otherwise Angular treats each tick as a
  // fresh insert and re-fires the :enter fade -> visible "wiggle").
  private _platformsCache: {
    key: string,
    list: FlatPlatform[]
  } | null = null;

  // Cached platform-preview gallery (one labelled screenshot per top-level
  // platform, in coverage order). Same-reference-until-key-changes so the
  // *ngFor never re-runs its lazy image loads on every tick.
  private _previewCache: {
    key: string,
    list: { name: string; logo: string; src: string }[]
  } | null = null;

  /** Every platform shown in the banner strip, in the same coverage order, each
   *  with its screenshot - so the "Platforms" gallery stays consistent with the
   *  banner (editor variants like Cursor / Antigravity / VS Codium and LINE all
   *  appear, sharing their parent listing's screenshot). Screenshot-less
   *  platforms are skipped. */
  get platformPreviews(): { name: string; logo: string; src: string }[] {
    const key = this.product?.key ?? '';
    if ( this._previewCache && this._previewCache.key === key ) {
      return this._previewCache.list;
    }
    const list: { name: string; logo: string; src: string }[] = [];
    for ( const fp of this.getVisiblePlatforms() ) {
      const src = fp.screenshot?.src;
      if ( !src ) {
        continue;
      }
      list.push( { name: fp.name, logo: fp.logo, src } );
    }
    this._previewCache = { key, list };
    return list;
  }

  /** Flattens product.platforms (incl. supportedPlatforms) and returns ALL
   * platforms. Returns the same array reference until product.key changes. */
  getVisiblePlatforms(): FlatPlatform[] {
    const key = this.product?.key ?? '';
    if ( this._platformsCache && this._platformsCache.key === key ) {
      return this._platformsCache.list;
    }
    this._platformsCache = { key, list: this.flattenPlatforms() };
    return this._platformsCache.list;
  }

  /** trackBy for the platforms *ngFor - stable identity keeps Angular from
   * replacing DOM nodes (and re-running the entrance fade) needlessly. */
  trackByPlatformName( _index: number, plat: { name: string } ): string {
    return plat.name;
  }

  /** trackBy for the bundle-includes grid so platform changes only add/remove
   *  the themes that differ instead of re-rendering every card. */
  trackByThemeKey( _index: number, theme: { key: string } ): string {
    return theme.key;
  }

  /** Walks product.platforms one level deep into supportedPlatforms so each
   * IDE/editor variant becomes its own pill. Each flat entry carries the
   * platform's own screenshot. */
  private flattenPlatforms(): FlatPlatform[] {
    const out: FlatPlatform[] = [];
    if ( !this.product?.platforms ) {
      return out;
    }
    for ( const platform of sortPlatformsByCoverage( this.product.platforms ) ) {
      const info: any = platform.info;
      const screenshot = platform.screenshot;
      if ( info?.supportedPlatforms?.length > 0 ) {
        for ( const sp of info.supportedPlatforms ) {
          out.push( {
            name: sp.name,
            logo: sp.logo,
            url: platform.url,
            downloadable: info.downloadable,
            screenshot,
            parentName: info.name,
          } );
        }
      } else if ( info ) {
        out.push( {
          name: info.name,
          logo: info.logo,
          url: platform.url,
          downloadable: info.downloadable,
          screenshot,
        } );
      }
    }
    return out;
  }

  /** Currently-selected flat platform (or null if none). */
  get selectedPlatform(): FlatPlatform | null {
    return this.getVisiblePlatforms()[this.selectedPlatformIndex] ?? null;
  }

  /** Screenshot to show in the banner - taken from the selected platform. */
  get selectedScreenshot(): any {
    return this.selectedPlatform?.screenshot ?? null;
  }

  /** the theme's review bubbles (owner, 2026-10-04: like the home page's wall, this theme's only) */
  reviewCards: Review[] = [];
  /** the bundle's sidebar swatch: a few of its themes' accents, set once per product */
  bundleSwatch: string[] = [];
  /** the theme's ratings on the marketplaces (JetBrains, VS Code, ...), beside the site's buyers' */
  reviewOtherCount = 0;

  /** Exposed to the template (replaces the removed `openSource` field). */
  readonly isThemeFree = isThemeFree;
  /** "New" for an unrated free theme, "N/A" for premium (no solo listing). */
  readonly noRatingLabel = noRatingLabel;

  /** every label the CTA can wear: stacked in the button so it keeps the widest one's width (owner, 2026-09-20) */
  readonly ctaLabels = PLATFORM_CTA_LABELS;

  /** CTA label for the selected platform (per-platform monetization). */
  get ctaText(): string {
    return platformCtaLabel( this.product, this.selectedPlatformKey, this.storeService.availability( this.selectedSku ) );
  }

  /** a store listing the store has said it cannot sell: the CTA is inert (Coming soon); it stays active while the store is still answering */
  get selectedStoreUnavailable(): boolean {
    const sku = this.selectedSku;
    return !!sku && !this.storeService.canAdd( sku );
  }

  /** The top-level platform name the selected pill belongs to (editor variants
   *  under Open VSX map to their parent listing). */
  private get selectedPlatformKey(): string | undefined {
    const plat = this.selectedPlatform;
    return plat?.parentName || plat?.name;
  }

  /** Store SKU of the selected platform when it is sold on the site. */
  get selectedSku(): string | undefined {
    return platformSku( this.product, this.selectedPlatformKey );
  }

  /** The version of the selected listing: the live catalogue for a listing sold
   *  here (the generated catalogue snapshot until it answers), the published
   *  artifact's version for every other platform (platform-version.data.ts).
   *  '' when that platform has no version to show (LINE). */
  get selectedVersion(): string {
    const sku = this.selectedSku;
    if ( sku ) return this.versionBySku[ sku ] || STORE_PLATFORM_VERSIONS[ sku ] || '';
    const platform = this.selectedPlatformKey;
    if ( !platform || !this.product?.key ) return '';
    return ( PLATFORM_VERSIONS[ this.product.key ] || {} )[ platform ] || '';
  }

  /** "$1.99" for a listing sold on the site: the live catalogue price, else the list price of product.data. */
  get selectedPriceLabel(): string | null {
    const sku = this.selectedSku;
    if ( !sku ) return null;
    return this.storeService.priceLabel( sku, platformListPrice( this.product, this.selectedPlatformKey ) );
  }

  /** The flag beside the pill: FREE for a free listing, the price for one sold
   *  on the site (no price known = no flag), hidden for a paid listing on
   *  another store. */
  get flagVisible(): boolean {
    if ( !this.selectedPaid ) return true;
    return !!this.selectedSku && !!this.selectedPriceLabel;
  }

  get flagLabel(): string {
    if ( !this.selectedPaid ) return 'FREE';
    return this.selectedPriceLabel || '';
  }

  /** Add to cart: puts the listing in the cart and opens the mini cart (a toast on a phone); the page stays. */
  buySelected(): void {
    const sku = this.selectedSku;
    if ( !sku ) return;
    this.cartPopupService.add( sku, 'theme-page' );
    this.monitorProviderService.recordClick( this.product.key, this.selectedPlatformKey || '' ).subscribe( {
      error: ( err ) => console.error( 'Failed to record platform click:', err )
    } );
  }

  /** Whether the selected platform is paid (hides the "Free" ribbon). */
  get selectedPaid(): boolean {
    return isPlatformPaid( this.product, this.selectedPlatform?.name );
  }

  /** Whether the selected platform is only planned (CTA inactive, "Coming soon"). */
  get selectedPlatformPlanned(): boolean {
    return isPlatformPlanned( this.product, this.selectedPlatform?.name ) || this.selectedStoreUnavailable;
  }

  /**
   * For a bundle product (isBundle): the individual themes its currently-selected
   * platform ships. Explicit `platform.bundledThemes` wins; otherwise a paid
   * platform includes every theme and a free platform only the free ones. Each
   * returned theme carries its `category` (for the card). [] for non-bundles.
   */
  get bundleIncludedThemes(): Product[] {
    if ( !this.product?.isBundle ) {
      return [];
    }
    const all: Product[] = [];
    for ( const line of THEMES ) {
      for ( const theme of line.products ) {
        // A bundle never lists itself or another bundle among its themes.
        if ( theme.key === this.product.key || theme.isBundle ) {
          continue;
        }
        all.push( { ...theme, category: line.name } );
      }
    }
    const platName = this.selectedPlatform?.name;
    const entry = this.product.platforms?.find( p => p.info?.name === platName );
    if ( entry?.bundledThemes?.length ) {
      return all.filter( t => entry.bundledThemes.includes( t.key ) );
    }
    return entry?.paid === true ? all : all.filter( t => isThemeFree( t ) );
  }

  /** Category / appearance breakdown of the bundle's included themes, for the
   *  "This bundle includes" description paragraph. */
  get bundleStats(): { total: number, nature: number, cities: number, dark: number, light: number } {
    const themes = this.bundleIncludedThemes;
    const count = ( pred: ( t: Product ) => boolean ) => themes.filter( pred ).length;
    return {
      total: themes.length,
      nature: count( t => t.category === 'Nature' ),
      cities: count( t => t.category === 'Cities' ),
      dark: count( t => /dark/i.test( t.appearance || '' ) ),
      light: count( t => /light/i.test( t.appearance || '' ) ),
    };
  }

  /** Composite key for the slide animation - re-fires the transition when
   * the user picks a different platform or the product changes. */
  get slideKey(): string {
    return `${this.product?.key ?? ''}-${this.selectedPlatformIndex}`;
  }

  /** User clicked a platform icon in the left column - show its screenshot
   * and stop the auto-cycle for good (matches home-page behaviour). */
  /** the ?platform= the page opened on: the top box opens on it and holds it */
  platformParam?: string;

  /** the top box's platform on show (owner 2026-10-15: the home's themes box here): the page follows it */
  onBoxPlatform( name: string ): void {
    const idx = this.getVisiblePlatforms().findIndex( p => p.name === name );
    if ( idx >= 0 ) {
      this.stopScreenshotCarousel();
      this.selectedPlatformIndex = idx;
    }
  }

  selectPlatform( idx: number ): void {
    // Clicking any platform pins the user's choice: halt the auto-cycle
    // permanently (like home-page's stopThemeRotation) instead of restarting
    // the interval. Stop first so even re-clicking the current platform stops.
    this.stopScreenshotCarousel();
    if ( this.selectedPlatformIndex === idx ) {
      return;
    }
    this.selectedPlatformIndex = idx;
  }

  /** Click on the Try Now / Get Now CTA: same effect as clicking the
   * platform pill (record click + open URL or routed page). */
  tryNowSelected(): void {
    const plat = this.selectedPlatform;
    if ( !plat ) {
      return;
    }
    if ( this.selectedSku ) {
      this.buySelected();
      return;
    }
    this.onPlatformClick( plat.name, plat.url, plat.downloadable );
  }

  sanitize(value: string): string {
    return value.replace(/<[^>]*>?/gm, '');
  }

  setBannerImageLoaded = () => {
    this.bannerImageLoaded = true;
  };

  setScreenshotLoaded = ( screenshot: any ) => {
    screenshot.loaded = true;
  };

  /**
   * Promote the next screenshot only once its image is fully decoded,
   * so the slide animation runs against the new screenshot - never the
   * outgoing one.
   */
  ngDoCheck(): void {
    const target = this.selectedScreenshot;
    const src = target?.src ?? '';
    if ( src === this.lastTargetScreenshotSrc ) {
      return;
    }
    this.lastTargetScreenshotSrc = src;
    this.preloadAndCommitScreenshot( target );
  }

  private preloadAndCommitScreenshot( target: any ): void {
    if ( !target?.src ) {
      this.displayedScreenshot = target;
      return;
    }
    const src = target.src;
    const img = new window.Image();
    const commit = () => {
      if ( this.lastTargetScreenshotSrc !== src ) {
        return;
      }
      this.displayedScreenshot = target;
      target.loaded = true;
    };
    img.onload = commit;
    img.onerror = commit;
    img.src = src;
    if ( img.complete && img.naturalHeight > 0 ) {
      commit();
    }
  }

  /** Slide key derived from the committed screenshot, so the slide
   *  animation only fires when the new image is ready to paint. */
  get displayedSlideKey(): string {
    return this.displayedScreenshot?.src ?? '';
  }

  isSmallScreen() {
    return this.utilService.isSmallScreen();
  }
}
